import { ChevronDown, MapPin, Navigation, Phone, Search, Star, X } from "lucide-react";
import storeCatalogLogo from "@/shared/assets/images/store-catalog-logo.png";
import { getGoogleMapDirectionsLink } from "../../utils";
import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent } from "@/shared/widgets/sheet";
import { Dialog, DialogClose, DialogContent } from "@/shared/widgets/dialog";
import { Input } from "@/shared/widgets/input";
import { buildStoreCatalogUrl } from "../../utils/plpUrl";
import { PLP_MAX_WIDTH } from '../../constants';
import { useNavigate } from "react-router-dom";
import { AppLink } from "@/shared/components/AppLink";
import useEmblaCarousel from "embla-carousel-react";
import { useIsDesktop } from "@/core/hooks/useMediaQuery";
import {
  findCityKey,
  getCityList,
  getStoreList,
  titleFromSlug,
  toKebabCase,
} from "@/shared/features/physical-store/utils/storeListUtils";

export type StoreWidgetData = {
  id?: number | string;
  sid?: string;
  storeName?: string;
  address?: string;
  cityName?: string;
  cityGroup?: string;
  postCode?: string;
  contactNumber?: string;
  destinationPlaceId?: string;
  storeTimings?: string;
  storeReviewLink?: string | null;
  storeUrl?: string;
};

export function SelectedStoreWidget({
  selectedStore,
  selectedStores,
  isLoading = false,
  storeCitySlug,
  cityToStoresMap,
  totalCount,
}: {
  selectedStore?: StoreWidgetData | null;
  selectedStores?: StoreWidgetData[];
  isLoading?: boolean;
  /** When on /stores/<city>/catalog.html (sidType=0), this is the <city> segment (e.g. "new-delhi"). */
  storeCitySlug?: string;
  /** Full city -> stores map from the physical stores API (used to populate dropdowns). */
  cityToStoresMap?: unknown;
  totalCount?: number;
}) {
  const navigate = useNavigate();
  const cityFromSlug = storeCitySlug ? titleFromSlug(storeCitySlug) : '';

  const cityList = useMemo(() => getCityList(cityToStoresMap), [cityToStoresMap]);
  const cityKeyFromSlug = useMemo(
    () => findCityKey(cityToStoresMap, storeCitySlug) ?? findCityKey(cityToStoresMap, cityFromSlug),
    [cityToStoresMap, cityFromSlug, storeCitySlug]
  );
  const [selectedCityKey, setSelectedCityKey] = useState<string>(cityKeyFromSlug ?? cityList[0] ?? "");
  // Header city should reflect the city of the loaded page (URL / selected store),
  // not the pending city selection from the city sheet.
  const headerCityName = selectedStore?.cityName?.trim() || cityFromSlug || "";
  const displayCityName = headerCityName || "";
  const storeList = useMemo(
    () => getStoreList(cityToStoresMap, selectedCityKey),
    [cityToStoresMap, selectedCityKey]
  );
  const [selectedStoreSids, setSelectedStoreSids] = useState<string[]>([]);
  const [isStoreSheetOpen, setIsStoreSheetOpen] = useState(false);
  const [storeSearch, setStoreSearch] = useState("");
  const [isCitySheetOpen, setIsCitySheetOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const isDesktop = useIsDesktop();
  const [isDesktopDialogOpen, setIsDesktopDialogOpen] = useState(false);
  const [desktopDialogPanel, setDesktopDialogPanel] = useState<'city' | 'store'>('city');

  const openCitySelector = () => {
    setCitySearch('');
    if (isDesktop) { setDesktopDialogPanel('city'); setIsDesktopDialogOpen(true); }
    else { setIsCitySheetOpen(true); }
  };
  const openStoreSelector = () => {
    setStoreSearch('');
    if (isDesktop) { setDesktopDialogPanel('store'); setIsDesktopDialogOpen(true); }
    else { setIsStoreSheetOpen(true); }
  };

  const filteredCityList = useMemo(() => {
    const q = citySearch.trim().toLowerCase();
    if (!q) return cityList;
    return cityList.filter((c) => c.toLowerCase().includes(q));
  }, [cityList, citySearch]);

  useEffect(() => {
    // When map arrives async, initialize/select the city so the left dropdown isn't blank.
    if (selectedStore) return;
    if (!selectedCityKey || !cityList.includes(selectedCityKey)) {
      const next = cityKeyFromSlug ?? cityList[0] ?? "";
      if (next) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- initialize selected city after async cityToStoresMap arrives
        setSelectedCityKey(next);
      }
    }
  }, [cityKeyFromSlug, cityList, selectedCityKey, selectedStore]);

  useEffect(() => {
    // When city changes (or initializes), reset store selection.
    if (!selectedStore) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset store selection when city changes in-sheet
      setSelectedStoreSids([]);
    }
  }, [selectedCityKey, selectedStore]);

  const filteredStoreList = useMemo(() => {
    const q = storeSearch.trim().toLowerCase();
    if (!q) return storeList;
    return storeList.filter((store) => {
      const name = store?.storeName?.trim() ?? "";
      return name.toLowerCase().includes(q);
    });
  }, [storeList, storeSearch]);

  const handleDesktopApply = () => {
    setIsDesktopDialogOpen(false);
    if (selectedStoreSids.length === 1) {
      const onlySidRaw = selectedStoreSids[0];
      const store = storeList.find(
        (s) => normalizeSidForCompare(String(s?.sid ?? '')) === normalizeSidForCompare(onlySidRaw)
      );
      if (!store) return;
      navigate(buildStoreCatalogUrl({ storeName: store.storeName, cityName: selectedCityKey, storeId: onlySidRaw }), { replace: false });
      return;
    }
    if (selectedStoreSids.length > 1) {
      const citySlug = toKebabCase(selectedCityKey);
      if (!citySlug) return;
      const sidParam = encodeSidsToQueryParam(selectedStoreSids);
      navigate(`/stores/${citySlug}-reslnk-${sidParam}/catalog.html`, { replace: false });
      return;
    }
    const citySlug = toKebabCase(selectedCityKey);
    if (citySlug) navigate(`/stores/${citySlug}/catalog.html`, { replace: false });
  };

  const storesForCarousel = useMemo(() => {
    if (Array.isArray(selectedStores) && selectedStores.length > 0) return selectedStores;
    return selectedStore ? [selectedStore] : [];
  }, [selectedStore, selectedStores]);
  const hasStoreCarousel = storesForCarousel.length > 1;
  const [activeStoreIndex, setActiveStoreIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const resolvedActiveStoreIndex = hasStoreCarousel ? activeStoreIndex : 0;

  useEffect(() => {
    if (!hasStoreCarousel) return;
    if (!emblaApi) return;
    const onSelect = () => setActiveStoreIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    const timer = window.setInterval(() => {
      emblaApi.scrollNext();
    }, 3500);
    return () => {
      window.clearInterval(timer);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, hasStoreCarousel]);

  const activeStore = storesForCarousel[resolvedActiveStoreIndex] ?? selectedStore ?? null;

  /** API often returns `storeName` as "Area, City" — title uses the part before the first comma; city line uses `cityName`. */
  const displayStoreName =
    activeStore?.storeName?.trim()?.split(",")[0]?.trim() || "";
  const displayAddress = activeStore?.address?.trim() || "";
  const displayPostCode = activeStore?.postCode?.trim() || "";
  const displayTimings = activeStore?.storeTimings?.trim() ?? '';

  const contactDigits = activeStore?.contactNumber ? activeStore.contactNumber.replace(/\D/g, "") : "";
  const callHref = contactDigits ? `tel:${contactDigits}` : undefined;

  const directionsHref =
    activeStore?.destinationPlaceId
      ? getGoogleMapDirectionsLink(activeStore.destinationPlaceId)
      : activeStore?.address
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activeStore.address)}`
        : undefined;

  const reviewsHref = activeStore?.storeReviewLink ?? undefined;

  const renderStoreActions = (store: StoreWidgetData) => {
    const digits = store.contactNumber ? store.contactNumber.replace(/\D/g, "") : "";
    const storeCallHref = digits ? `tel:${digits}` : undefined;
    const storeDirectionsHref =
      store.destinationPlaceId
        ? getGoogleMapDirectionsLink(store.destinationPlaceId)
        : store.address
          ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.address)}`
          : undefined;
    const storeReviewsHref = store.storeReviewLink ?? undefined;

    return (
      <div className={`mt-4 grid w-full gap-2 ${storeReviewsHref ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {storeCallHref ? (
          <a
            href={storeCallHref}
            className="rounded-lg py-3 text-xs font-semibold uppercase tracking-normal text-white flex items-center justify-center gap-1"
            style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
            aria-label="Call store"
          >
              <Phone className="h-4 w-4" aria-hidden />
              Call
          </a>
        ) : (
          <button
            type="button"
            disabled
            className="rounded-lg py-3 text-xs font-semibold uppercase tracking-normal text-white/70 flex items-center justify-center gap-1"
            style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
          >
              <Phone className="h-4 w-4" aria-hidden />
              Call
          </button>
        )}

        {storeDirectionsHref ? (
          <a
            href={storeDirectionsHref}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg py-3 text-xs font-semibold uppercase tracking-normal text-white flex items-center justify-center gap-1"
            style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
            aria-label="Get directions"
          >
              <Navigation className="h-4 w-4" aria-hidden />
              Directions
          </a>
        ) : (
          <button
            type="button"
            disabled
            className="rounded-lg py-3 text-xs font-semibold uppercase tracking-normal text-white/70 flex items-center justify-center gap-1"
            style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
          >
              <Navigation className="h-4 w-4" aria-hidden />
              Directions
          </button>
        )}

        {storeReviewsHref ? (
          <a
            href={storeReviewsHref}
            rel="noopener noreferrer nofollow"
            className="rounded-lg py-3 text-xs font-semibold uppercase tracking-normal text-white flex items-center justify-center gap-1"
            style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
            aria-label="Store reviews"
          >
              <Star className="h-4 w-4" aria-hidden />
              Reviews
          </a>
        ) : null}
      </div>
    );
  };

  return (
    <section>
      {/* ── Desktop layout (≥ md) ──────────────────────────────── */}
      <div
        className={`hidden md:block w-full overflow-hidden mx-auto max-w-[${PLP_MAX_WIDTH}px] px-4`}
        
      >
        <div className={`mx-auto flex w-full max-w-[${PLP_MAX_WIDTH}px] items-stretch`} style={{ backgroundColor: "var(--store-plp-bg)" }}>
        {isLoading ? (
          <div className="flex w-full animate-pulse items-stretch">
            <div className="w-[25%] px-8 py-7">
              <div className="h-5 w-4/5 rounded bg-gray-300/50" />
              <div className="mt-3 h-5 w-3/4 rounded bg-gray-300/50" />
              <div className="mt-3 h-5 w-2/3 rounded bg-gray-300/50" />
            </div>
            <div className="flex w-[18%] items-center justify-center py-4">
              <div className="h-32 w-32 rounded bg-gray-300/50" />
            </div>
            <div className="flex flex-1 flex-col justify-center px-8 py-7">
              <div className="h-4 w-2/5 rounded bg-gray-300/50" />
              <div className="mt-2 h-3 w-3/4 rounded bg-gray-300/50" />
              <div className="mt-4 flex gap-3">
                <div className="h-10 w-20 rounded-full bg-gray-300/50" />
                <div className="h-10 w-32 rounded-full bg-gray-300/50" />
                <div className="h-10 w-24 rounded-full bg-gray-300/50" />
              </div>
            </div>
            <div className="flex w-[25%] items-center justify-center px-6 py-7">
              <div className="h-10 w-36 rounded bg-gray-300/50" />
            </div>
          </div>
        ) : activeStore ? (
          <>
            {/* 1. Left: tagline */}
            <div className="flex w-[25%] items-center px-8 py-7">
              <p className="text-xl font-semibold leading-snug text-[#c4a7b8]">
                {totalCount != null && totalCount > 0 ? `${totalCount} ` : ''}
                {totalCount != null && totalCount > 0
                  ? `Jewellery Designs in BlueStone ${displayStoreName}${displayCityName ? `, ${displayCityName}` : ''} store`
                  : `Jewellery Designs in BlueStone ${displayStoreName}${displayCityName ? `, ${displayCityName}` : ''} store`}
              </p>
            </div>

            {/* 2. Center: store illustration */}
            <div className="flex w-[18%] items-center justify-center py-3">
              <img
                src={storeCatalogLogo}
                alt="BlueStone store"
                className="h-36 w-auto object-contain"
              />
            </div>

            {/* 3. Address + action buttons */}
            <div className="flex flex-1 flex-col justify-center px-8 py-7">
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gray-600" aria-hidden />
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {displayStoreName}{displayCityName ? `, ${displayCityName}` : ''}
                  </p>
                  <p className="mt-0.5 text-sm text-gray-700">
                    {displayAddress}{displayPostCode ? `, ${displayPostCode}` : ''}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-nowrap items-center gap-2 xl:gap-3">
                {callHref ? (
                  <a
                    href={callHref}
                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-xs font-semibold text-white xl:gap-2 xl:px-5 xl:py-2.5 xl:text-sm"
                    style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
                    aria-label="Call store"
                  >
                    <Phone className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="hidden lg:inline">Call</span>
                  </a>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-xs font-semibold text-white/50 xl:gap-2 xl:px-5 xl:py-2.5 xl:text-sm"
                    style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
                  >
                    <Phone className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="hidden lg:inline">Call</span>
                  </button>
                )}
                {directionsHref ? (
                  <a
                    href={directionsHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-xs font-semibold text-white xl:gap-2 xl:px-5 xl:py-2.5 xl:text-sm"
                    style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
                    aria-label="Get directions"
                  >
                    <Navigation className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="hidden lg:inline xl:hidden">Directions</span>
                    <span className="hidden xl:inline">Get Directions</span>
                  </a>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-xs font-semibold text-white/50 xl:gap-2 xl:px-5 xl:py-2.5 xl:text-sm"
                    style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
                  >
                    <Navigation className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="hidden lg:inline xl:hidden">Directions</span>
                    <span className="hidden xl:inline">Get Directions</span>
                  </button>
                )}
                {reviewsHref
                  ? (
                    <a
                      href={reviewsHref}
                      rel="noopener noreferrer nofollow"
                      className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-xs font-semibold text-white xl:gap-2 xl:px-5 xl:py-2.5 xl:text-sm"
                      style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
                      aria-label="Store reviews"
                    >
                      <Star className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="hidden lg:inline">Reviews</span>
                    </a>
                  )
                  : null}
              </div>
            </div>

            {/* 4. Far right: view all stores in city */}
            {displayCityName ? (
              <div className="flex w-[25%] shrink-0 items-center justify-center py-7 pl-6 pr-8">
                <AppLink
                  to={`/stores/${toKebabCase(displayCityName)}/catalog.html`}
                  shouldRefresh={false}
                  className="whitespace-nowrap rounded-lg border border-gray-400 bg-transparent px-5 py-3 text-center text-sm font-medium text-gray-700 transition-colors hover:bg-white/40"
                >
                  View all stores in {displayCityName}
                </AppLink>
              </div>
            ) : null}
          </>
        ) : (
          /* Desktop: city page – 4-column layout */
          <>
            {/* Col 1: count + city label */}
            <div className="flex w-[25%] items-center px-8 py-8">
              <p className="text-xl font-semibold leading-snug text-[#c4a7b8]">
                {totalCount != null && totalCount > 0 ? `${totalCount} ` : ''}
                Jewellery Designs
                {displayCityName ? <><br />in {displayCityName}</> : ''}
              </p>
            </div>

            {/* Col 2: store illustration */}
            <div className="flex w-[25%] items-center justify-center py-3">
              <img
                src={storeCatalogLogo}
                alt="BlueStone store"
                className="h-36 w-auto object-contain"
              />
            </div>

            {/* Col 3: hero text */}
            <div className="flex flex-1 flex-col justify-center gap-4 px-4 py-7">
              <div>
                <p className="text-lg text-[#c4a7b8]">Browse all Designs</p>
                <p className="text-2xl font-bold text-[#40628c]">in {displayCityName || 'India'}</p>
              </div>
            </div>

            {/* Col 4: view all stores — opens store selector dialog */}
            {displayCityName ? (
              <div className="flex w-[25%] shrink-0 items-center justify-center p-7">
                <button
                  type="button"
                  className="whitespace-nowrap rounded-lg border border-gray-400 bg-transparent px-5 py-3 text-center text-sm font-medium text-gray-700 transition-colors hover:bg-white/40"
                  onClick={() => { setDesktopDialogPanel('city'); setIsDesktopDialogOpen(true); }}
                >
                  View all stores in {displayCityName}
                </button>
              </div>
            ) : null}
          </>
        )}
        </div>
      </div>

      {/* ── Mobile card (< md) ─────────────────────────────────── */}
      <div className="px-4 pt-4 pb-4 md:hidden">
        <div className="mx-auto max-w-screen-md overflow-hidden rounded-lg" style={{ backgroundColor: "var(--store-plp-bg)" }}>
          <div className="flex flex-col items-center px-4 pb-4 pt-3">
            <div className="flex h-24 w-full items-center justify-center">
              <img
                src={storeCatalogLogo}
                alt=""
                className="h-full w-auto object-contain"
                aria-hidden
                height="170px"
              />
            </div>

          <div className="mt-3 w-full text-center">
            {isLoading ? (
              <div className="animate-pulse">
                <div className="mx-auto h-7 w-2/3 rounded bg-gray-300/60" />
                <div className="mx-auto mt-3 h-4 w-[92%] rounded bg-gray-300/50" />
                <div className="mx-auto mt-2 h-4 w-3/4 rounded bg-gray-300/50" />
                <div className="mx-auto mt-5 h-4 w-1/2 rounded bg-gray-300/50" />
                <div className="mt-4 grid w-full grid-cols-3 gap-2">
                  <div className="h-11 rounded-lg bg-gray-300/50" />
                  <div className="h-11 rounded-lg bg-gray-300/50" />
                  <div className="h-11 rounded-lg bg-gray-300/50" />
                </div>
              </div>
            ) : null}

            {!isLoading ? (
              <>
                {hasStoreCarousel ? (
                  <div className="mb-2 overflow-hidden" ref={emblaRef}>
                    <div className="flex">
                      {storesForCarousel.map((store, idx) => (
                        <div key={`${store.sid ?? store.storeName ?? idx}`} className="min-w-0 flex-[0_0_100%] px-1">
                          <div className="text-lg font-bold tracking-tight text-gray-900">
                            {store.storeName?.trim()?.split(",")[0]?.trim() || "Selected Store"}
                          </div>
                          <div className="mt-1 text-sm text-gray-700">
                            {store.address?.trim() || "Address not available"}
                          </div>
                          <div className="mt-0.5 text-sm text-gray-700">
                            {(store.cityName?.trim() || displayCityName)}
                            {store.postCode?.trim() ? ` ${store.postCode?.trim()}` : ""}
                          </div>
                          {store.storeTimings?.trim() && (
                          <div className="mt-4 w-full border-t border-white/60 pt-3 text-center text-sm text-gray-700">
                            {store.storeTimings.trim().startsWith("Store timings:")
                              ? store.storeTimings.trim()
                              : `Store timings: ${store.storeTimings.trim()}`}
                          </div>
                          )}
                          {renderStoreActions(store)}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                {displayStoreName ? (
                  <>
                    {!hasStoreCarousel ? (
                      <>
                        <div className="text-lg font-bold tracking-tight text-gray-900">{displayStoreName}</div>
                        <div className="mt-1 text-sm text-gray-700">
                          {displayAddress || "Address not available"}
                        </div>
                        <div className="mt-0.5 text-sm text-gray-700">
                          {displayCityName}
                          {displayPostCode ? ` ${displayPostCode}` : ""}
                        </div>
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                    <div className="text-lg font-bold tracking-tight text-gray-900">
                      {totalCount != null && displayCityName
                        ? `All ${totalCount} Designs in ${displayCityName}`
                        : displayCityName
                          ? `All Designs in ${displayCityName}`
                          : 'All Designs'}
                    </div>
                  </>
                )}

              </>
            ) : null}
          </div>

          {selectedStore && !hasStoreCarousel && !isLoading && displayTimings ? (
            <div className="mt-4 w-full border-t border-white/60 pt-3 text-center text-sm text-gray-700">
              {displayTimings.startsWith("Store timings:") ? displayTimings : `Store timings: ${displayTimings}`}
            </div>
          ) : null}

          {hasStoreCarousel && !isLoading ? (
            <div className="mt-2 flex items-center justify-center gap-1.5">
              {storesForCarousel.map((store, idx) => (
                <button
                  key={`dot-${store.sid ?? idx}`}
                  type="button"
                  className={[
                    "h-1.5 rounded-full transition-all",
                    idx === resolvedActiveStoreIndex ? "w-4 bg-gray-700" : "w-1.5 bg-gray-400",
                  ].join(" ")}
                  aria-label={`Go to store ${idx + 1}`}
                  onClick={() => emblaApi?.scrollTo(idx)}
                />
              ))}
            </div>
          ) : null}

          {selectedStore && !hasStoreCarousel && !isLoading ? (
          <div className={`mt-4 grid w-full gap-2 ${reviewsHref ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {callHref ? (
              <a
                href={callHref}
                className="rounded-lg py-3 text-xs font-semibold uppercase tracking-normal text-white flex items-center justify-center gap-1"
                style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
                aria-label="Call store"
              >
                <Phone className="h-4 w-4" aria-hidden />
                Call
              </a>
            ) : (
              <button
                type="button"
                disabled
                className="rounded-lg py-3 text-xs font-semibold uppercase tracking-normal text-white/70 flex items-center justify-center gap-1"
                style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
              >
                  <Phone className="h-4 w-4" aria-hidden />
                  Call
              </button>
            )}

            {directionsHref ? (
              <a
                href={directionsHref}
                target="_blank"
                rel="noreferrer"
                className="rounded-lg py-3 text-xs font-semibold uppercase tracking-normal text-white flex items-center justify-center gap-1"
                style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
                aria-label="Get directions"
              >
                  <Navigation className="h-4 w-4" aria-hidden />
                  Directions
              </a>
            ) : (
              <button
                type="button"
                disabled
                className="rounded-lg py-3 text-xs font-semibold uppercase tracking-normal text-white/70 flex items-center justify-center gap-1"
                style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
              >
                  <Navigation className="h-4 w-4" aria-hidden />
                  Directions
              </button>
            )}

            {reviewsHref ? (
              <a
                href={reviewsHref}
                rel="noopener noreferrer nofollow"
                className="rounded-lg py-3 text-xs font-semibold uppercase tracking-normal text-white flex items-center justify-center gap-1"
                style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
                aria-label="Store reviews"
              >
                  <Star className="h-4 w-4" aria-hidden />
                  Reviews
              </a>
            ) : null}
          </div>
          ) : null}

          {selectedStore && displayCityName && !isLoading ? (
            <AppLink
              to={`/stores/${toKebabCase(displayCityName)}/catalog.html`}
              shouldRefresh={false}
              className="mt-4 w-full rounded-xl bg-white/80 py-3 text-center text-sm font-semibold text-[#4A607F]"
            >
              View all designs in {displayCityName}
            </AppLink>
          ) : null}

          {!selectedStore && !isLoading ? (
            <div className="mt-4 grid w-full grid-cols-2 gap-3">
              <label className="relative">
                <span className="sr-only">City</span>
                <button
                  type="button"
                  className="flex h-12 w-full min-w-0 items-center rounded-xl px-4 pr-12 text-left text-base text-white"
                  style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
                  onChange={(e) => {
                    void e;
                  }}
                  onClick={openCitySelector}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {selectedCityKey || "Select city"}
                  </span>
                </button>
                <span
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-2"
                  aria-hidden
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </label>

              <label className="relative">
                <span className="sr-only">Store</span>
                <button
                  type="button"
                  className="flex h-12 w-full min-w-0 items-center rounded-xl px-4 pr-12 text-left text-base text-white"
                  style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
                  onClick={openStoreSelector}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {selectedStoreSids.length ? `${selectedStoreSids.length} selected` : "Select store"}
                  </span>
                </button>
                <span
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-2"
                  aria-hidden
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </label>
            </div>
          ) : null}
        </div>
      </div>
      </div>

      {/* ── Desktop selector dialog ─────────────────────────────── */}
      {!selectedStore ? (
        <Dialog open={isDesktopDialogOpen} onOpenChange={setIsDesktopDialogOpen}>
          <DialogContent
            className="hidden h-[60vh] max-w-[480px] overflow-hidden rounded-xl p-0 md:flex md:flex-col"
            showCloseButton={false}
          >
            {/* Tab row */}
            <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-4">
              <button
                type="button"
                className={[
                  'flex h-11 flex-1 items-center gap-2 rounded-lg border px-3 text-sm transition-colors',
                  desktopDialogPanel === 'city'
                    ? 'border-gray-400 bg-white text-gray-900'
                    : 'border-gray-200 bg-gray-50 text-gray-500',
                ].join(' ')}
                onClick={() => { setCitySearch(''); setDesktopDialogPanel('city'); }}
              >
                <Search className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-left">{selectedCityKey || 'Select city'}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
              </button>
              <button
                type="button"
                className={[
                  'flex h-11 flex-1 items-center gap-2 rounded-lg border px-3 text-sm transition-colors',
                  desktopDialogPanel === 'store'
                    ? 'border-gray-400 bg-white text-gray-900'
                    : 'border-gray-200 bg-gray-50 text-gray-500',
                ].join(' ')}
                onClick={() => { setStoreSearch(''); setDesktopDialogPanel('store'); }}
              >
                <Search className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-left">
                  {selectedStoreSids.length ? `${selectedStoreSids.length} selected` : 'Select store'}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
              </button>
              <DialogClose asChild>
                <button type="button" className="ml-1 rounded-full p-1 text-gray-400 hover:text-gray-700" aria-label="Close">
                  <X className="h-5 w-5" />
                </button>
              </DialogClose>
            </div>

            {/* List */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {desktopDialogPanel === 'city' ? (
                <>
                  <div className="sticky top-0 z-10 border-b border-gray-100 bg-white px-4 py-3">
                    <div className="relative">
                      <Input
                        value={citySearch}
                        onChange={(e) => setCitySearch(e.target.value)}
                        placeholder="Search city"
                        className="h-10 w-full rounded-lg bg-gray-50 px-4 pr-10 text-sm"
                        autoFocus
                      />
                      <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {filteredCityList.map((city) => {
                      const selected = city === selectedCityKey;
                      return (
                        <button
                          key={city}
                          type="button"
                          className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => {
                            setSelectedCityKey(city);
                            setSelectedStoreSids([]);
                            setDesktopDialogPanel('store');
                          }}
                        >
                          <span className={selected ? 'font-medium text-gray-900' : undefined}>{city}</span>
                          <span
                            className={[
                              'h-5 w-5 rounded-full border',
                              selected ? 'border-gray-600 bg-gray-600' : 'border-gray-300 bg-transparent',
                            ].join(' ')}
                            aria-hidden
                          />
                        </button>
                      );
                    })}
                    {filteredCityList.length === 0 ? (
                      <div className="px-5 py-6 text-sm text-gray-500">No cities found.</div>
                    ) : null}
                  </div>
                </>
              ) : (
                <>
                  <div className="sticky top-0 z-10 border-b border-gray-100 bg-white px-4 py-3">
                    <div className="relative">
                      <Input
                        value={storeSearch}
                        onChange={(e) => setStoreSearch(e.target.value)}
                        placeholder={`Search stores in ${selectedCityKey || 'city'}`}
                        className="h-10 w-full rounded-lg bg-gray-50 px-4 pr-10 text-sm"
                        autoFocus
                      />
                      <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {filteredStoreList.map((store) => {
                      const sidRaw = String(store?.sid ?? '').trim();
                      const sidKey = normalizeSidForCompare(sidRaw);
                      const isSelected = sidKey
                        ? selectedStoreSids.some((s) => normalizeSidForCompare(s) === sidKey)
                        : false;
                      const canSelectMore = selectedStoreSids.length < 5;
                      const disabled = !isSelected && !canSelectMore;
                      const name = store?.storeName?.trim() ?? 'Store';
                      return (
                        <button
                          key={sidKey || name}
                          type="button"
                          disabled={disabled}
                          className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          onClick={() => {
                            if (!sidKey) return;
                            setSelectedStoreSids((prev) => {
                              if (prev.some((x) => normalizeSidForCompare(x) === sidKey)) return prev.filter((x) => normalizeSidForCompare(x) !== sidKey);
                              if (prev.length >= 5) return prev;
                              return [...prev, sidRaw];
                            });
                          }}
                        >
                          <span className={isSelected ? 'font-medium text-gray-900' : undefined}>{name}</span>
                          <span
                            className={[
                              'h-5 w-5 rounded-full border-2',
                              isSelected ? 'border-gray-600 bg-gray-600' : 'border-gray-300 bg-transparent',
                            ].join(' ')}
                            aria-hidden
                          />
                        </button>
                      );
                    })}
                    {filteredStoreList.length === 0 ? (
                      <div className="px-5 py-6 text-sm text-gray-500">No stores found.</div>
                    ) : null}
                  </div>
                </>
              )}
            </div>

            {/* Apply */}
            <div className="border-t border-gray-200 bg-white px-4 py-4">
              <button
                type="button"
                className="w-full rounded-xl py-3.5 text-sm font-semibold uppercase tracking-wide text-white"
                style={{ backgroundColor: 'var(--store-plp-top-bar-bg)' }}
                onClick={handleDesktopApply}
              >
                Apply
              </button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}

      {!selectedStore ? (
        <Sheet open={isCitySheetOpen} onOpenChange={setIsCitySheetOpen}>
          <SheetContent
            side="bottom"
            className="h-[55vh] max-h-[55vh] overflow-hidden p-0"
          >
            <div
              className="flex h-full flex-col"
              style={{ backgroundColor: "var(--store-plp-bg)" }}
            >
              <div
                className="sticky top-0 z-10 px-4 pt-4 pb-3"
                style={{ backgroundColor: "var(--store-plp-bg)" }}
              >
                <div className="relative">
                  <Input
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    placeholder="Select your city"
                    className="h-12 w-full rounded-xl bg-white/60 px-4 pr-12 text-base outline-none"
                    autoFocus
                  />
                  <Search
                    className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500"
                    aria-hidden
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-6">
                <div className="divide-y divide-gray-300/60 rounded-xl bg-white/40">
                  {filteredCityList.map((city) => {
                    const selected = city === selectedCityKey;
                    return (
                      <button
                        key={city}
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left text-base text-gray-700"
                        onClick={() => {
                          setSelectedCityKey(city);
                          setSelectedStoreSids([]);
                          setIsCitySheetOpen(false);
                        }}
                      >
                        <span className={selected ? "text-gray-900" : undefined}>
                          {city}
                        </span>
                        <span
                          className={[
                            "h-5 w-5 rounded-full border",
                            selected ? "border-gray-500 bg-gray-500" : "border-gray-400 bg-transparent",
                          ].join(" ")}
                          aria-hidden
                        />
                      </button>
                    );
                  })}
                  {filteredCityList.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-gray-600">No cities found.</div>
                  ) : null}
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      ) : null}

      {!selectedStore ? (
        <Sheet open={isStoreSheetOpen} onOpenChange={setIsStoreSheetOpen}>
          <SheetContent
            side="bottom"
            className="h-[55vh] max-h-[55vh] overflow-hidden p-0"
          >
            <div
              className="flex h-full flex-col"
              style={{ backgroundColor: "var(--store-plp-bg)" }}
            >
              <div
                className="sticky top-0 z-10 px-4 pt-4 pb-3"
                style={{ backgroundColor: "var(--store-plp-bg)" }}
              >
                <div className="relative">
                  <Input
                    value={storeSearch}
                    onChange={(e) => setStoreSearch(e.target.value)}
                    placeholder={`Search BlueStone store(s) in ${selectedCityKey || headerCityName || "city"}`}
                    className="h-12 w-full rounded-xl bg-white/60 px-4 pr-12 text-base outline-none"
                    autoFocus
                  />
                  <Search
                    className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500"
                    aria-hidden
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-6">
                <div className="divide-y divide-gray-300/60 rounded-xl bg-white/40">
                  {filteredStoreList.map((store) => {
                    const sidRaw = String(store?.sid ?? "").trim();
                    const sidKey = normalizeSidForCompare(sidRaw);
                    const isSelected = sidKey
                      ? selectedStoreSids.some((s) => normalizeSidForCompare(s) === sidKey)
                      : false;
                    const canSelectMore = selectedStoreSids.length < 5;
                    const disabled = !isSelected && !canSelectMore;
                    const name = store?.storeName?.trim() ?? "Store";

                    return (
                      <button
                        key={sidKey || name}
                        type="button"
                        disabled={disabled}
                        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left text-base text-gray-700 disabled:opacity-60"
                        onClick={() => {
                          if (!sidKey) return;
                          setSelectedStoreSids((prev) => {
                            if (prev.some((x) => normalizeSidForCompare(x) === sidKey)) {
                              return prev.filter((x) => normalizeSidForCompare(x) !== sidKey);
                            }
                            if (prev.length >= 5) return prev;
                            return [...prev, sidRaw];
                          });
                        }}
                      >
                        <span className={isSelected ? "text-gray-900" : undefined}>
                          {name}
                        </span>
                        <span
                          className={[
                            "h-5 w-5 rounded-full border-2",
                            isSelected
                              ? "border-gray-500 bg-gray-500"
                              : "border-gray-400 bg-transparent",
                          ].join(" ")}
                          aria-hidden
                        />
                      </button>
                    );
                  })}

                  {filteredStoreList.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-gray-600">No stores found.</div>
                  ) : null}
                </div>
              </div>

              <div className="flex items-center justify-between px-6 py-4">
                <span className="text-sm text-gray-500">Select up to 5 stores</span>
                <button
                  type="button"
                  disabled={selectedStoreSids.length === 0}
                  className="rounded-xl px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white disabled:opacity-60"
                  style={{ backgroundColor: "var(--store-plp-top-bar-bg)" }}
                  onClick={() => {
                    setIsStoreSheetOpen(false);
                    // If user selected a single store, navigate to canonical store route:
                    // /stores/<store>-<city>-reslnk-<sid>/catalog.html
                    if (selectedStoreSids.length === 1) {
                      const onlySidRaw = selectedStoreSids[0];
                      const store = storeList.find(
                        (s) => normalizeSidForCompare(String(s?.sid ?? "")) === normalizeSidForCompare(onlySidRaw)
                      );
                      if (!store) return;
                      navigate(
                        buildStoreCatalogUrl({
                          storeName: store.storeName,
                          cityName: selectedCityKey,
                          // Use the exact selected SID value to avoid mismatches between list data
                          // and the applied selection (some APIs return different encodings per field).
                          storeId: onlySidRaw,
                        }),
                        { replace: false }
                      );
                      return;
                    }

                    // Multi-select: keep city route and pass selected SIDs via query param.
                    const citySlug = toKebabCase(selectedCityKey);
                    if (!citySlug) return;
                    const sidParam = encodeSidsToQueryParam(selectedStoreSids);
                    navigate(`/stores/${citySlug}-reslnk-${sidParam}/catalog.html`, { replace: false });
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      ) : null}
    </section>
  );
}

function normalizeSidForCompare(value: string | undefined | null): string {
  const raw = (value ?? "").toString().trim();
  if (!raw) return "";
  try {
    return decodeURIComponent(raw).replace(/=+$/g, "");
  } catch {
    return raw.replace(/=+$/g, "");
  }
}

function encodeSidsToQueryParam(sids: string[]): string {
  const clean = sids
    .map((s) => s.trim())
    .filter(Boolean)
    // Keep encoded SID stable for the same store set regardless of selection order.
    .sort((a, b) => a.localeCompare(b));
  const joined = clean.join(",");
  const base64 = globalThis.btoa(joined);
  return encodeURIComponent(base64);
}

